import { useState, useEffect } from 'preact/hooks';
import { defRoute } from '@components/router';
import { useCurrentTenant } from 'client/lib/auth';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';

export const route = defRoute({
  isPublic: true,
  Page,
});

type TrackingData = Record<string, string>;

type PurchaseResult = {
  pixelPayload: Record<string, unknown>;
  apiPayload: Record<string, unknown>;
  serverLog: string;
};

function Section(props: { title: string; subtitle?: string; children: preact.ComponentChildren }) {
  return (
    <div class="rounded-lg border border-gray-200 bg-white p-6">
      <h2 class="mb-1 text-lg font-semibold text-gray-900">{props.title}</h2>
      {props.subtitle && <p class="mb-4 text-sm text-gray-500">{props.subtitle}</p>}
      {!props.subtitle && <div class="mb-4" />}
      {props.children}
    </div>
  );
}

function JsonBlock(props: { data: unknown }) {
  return (
    <pre class="overflow-x-auto rounded-md bg-gray-50 p-4 text-xs text-gray-700">
      {JSON.stringify(props.data, undefined, 2)}
    </pre>
  );
}

function CheckItem(props: { label: string; detail?: string; done: boolean }) {
  return (
    <div class="flex items-start gap-2 py-1 text-sm">
      {props.done ? (
        <span class="mt-0.5 text-green-600">&#10003;</span>
      ) : (
        <span class="mt-0.5 text-gray-300">&#9675;</span>
      )}
      <div>
        <span class={props.done ? 'font-medium text-gray-900' : 'text-gray-400'}>
          {props.label}
        </span>
        {props.detail && <p class="text-xs text-gray-500">{props.detail}</p>}
      </div>
    </div>
  );
}

const DEMO_COURSE = {
  id: AIP_COURSE_ID,
  title: 'Audience from Scratch / AIP',
  price: '$997',
};

function Page() {
  const tenant = useCurrentTenant();
  const [tracking, setTracking] = useState<TrackingData>({});
  const [purchaseResult, setPurchaseResult] = useState<PurchaseResult | undefined>(undefined);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const affiliateKeys = ['orid', 'opid', 'aff', 'ref', 'utm_source'];
    const captured: TrackingData = {};
    for (const key of affiliateKeys) {
      const val = params.get(key);
      if (val) {
        captured[key] = val;
      }
    }
    if (Object.keys(captured).length > 0) {
      sessionStorage.setItem('affiliate_tracking', JSON.stringify(captured));
    }
    const raw = sessionStorage.getItem('affiliate_tracking');
    setTracking(raw ? JSON.parse(raw) : captured);
  }, []);

  function handlePurchase() {
    const trackingDomain = tenant.ontraportTrackingDomain || '';
    const demoEmail = 'student@example.com';
    const demoPurchaseId = 'demo-purchase-001';

    const pixelParams: Record<string, string> = {
      order_id: demoPurchaseId,
      email: demoEmail,
      item_id_1: DEMO_COURSE.id,
      item_external_id_1: DEMO_COURSE.id,
      item_price_1: '997.00',
      item_qty_1: '1',
    };

    const domain = trackingDomain.replace(/^https?:\/\//, '');
    const pixelPayload = {
      endpoint: `https://${domain}/p`,
      ...pixelParams,
      note: 'Ontraport matches this email to the contact record, which already has the affiliate from the original opt-in.',
    };

    console.info('[Ontraport Tracking] Partner tracking pixel fired:', pixelPayload);

    if (trackingDomain) {
      const img = new Image();
      img.src = `https://${domain}/p?${new URLSearchParams(pixelParams).toString()}`;
    }

    const apiPayload = {
      action: 'tagContactByName',
      endpoint: 'PUT https://api.ontraport.com/1/objects/tagByName',
      lookup: `GET https://api.ontraport.com/1/object/getByEmail?objectID=0&email=${demoEmail}`,
      tag_name: `Purchased - ${DEMO_COURSE.title}`,
      contact_email: demoEmail,
      note: 'Server-side API call tags the contact as purchased. Works even when cookies are gone.',
    };

    const serverLog = `[ontraport] tagPurchaser: email=${demoEmail} tag="Purchased - ${DEMO_COURSE.title}"`;

    setPurchaseResult({ pixelPayload, apiPayload, serverLog });
  }

  const hasTracking = Object.keys(tracking).length > 0;
  const hasOntraportScript = !!tenant.ontraportAccountId;

  return (
    <div class="mx-auto max-w-2xl px-4 py-8">
      <div class="mb-8">
        <h1 class="text-2xl font-bold text-gray-900">Affiliate Tracking Demo</h1>
        <p class="mt-1 text-sm text-gray-500">
          Shows what happens on Playground when a customer arrives from the webinar funnel and
          completes a purchase. The affiliate attribution lives in Ontraport from the original
          opt-in &mdash; this page shows how Playground closes the loop.
        </p>
      </div>

      <div class="flex flex-col gap-6">
        <Section
          title="Ontraport Tracking Script"
          subtitle="Loaded on every Playground page. Lets Ontraport recognize visitors via their cookie from the original opt-in."
        >
          {hasOntraportScript ? (
            <div class="flex items-center gap-2 rounded-md bg-green-50 p-3 text-sm text-green-700">
              <span>&#10003;</span>
              <span>
                Active &mdash; Ontraport account <strong>{tenant.ontraportAccountId}</strong>
              </span>
            </div>
          ) : (
            <div class="rounded-md bg-amber-50 p-3 text-sm text-amber-700">
              Not active &mdash; no Ontraport Account ID configured on this tenant.
            </div>
          )}
          <p class="mt-3 text-xs text-gray-500">
            This is the primary tracking mechanism. If the visitor has an Ontraport cookie (from
            clicking an affiliate link or opting into a webinar), Ontraport can recognize them here
            without any URL parameters.
          </p>
        </Section>

        <Section
          title="URL Parameters (Backup)"
          subtitle="Captured from the link and saved in the browser session as a fallback if the Ontraport cookie is missing."
        >
          {hasTracking ? (
            <div>
              <p class="mb-2 text-sm font-medium text-green-700">Captured from URL:</p>
              <JsonBlock data={tracking} />
              <p class="mt-3 text-xs text-gray-500">
                These are saved in the browser session and passed along with purchase events. Useful
                if the customer cleared cookies or switched devices since opting in.
              </p>
            </div>
          ) : (
            <div>
              <div class="rounded-md bg-gray-50 p-3 text-sm text-gray-500">
                No URL parameters detected &mdash; that's normal. In the typical webinar flow, the
                purchase link comes from Mirasee and may not carry affiliate params. The Ontraport
                tracking script + cookie handles attribution instead.
              </div>
              <p class="mt-3 text-xs text-gray-500">
                To test with params, add them to the URL, e.g.:{' '}
                <code class="rounded bg-gray-100 px-1">?orid=OP-42&amp;opid=webinar-feb</code>
              </p>
            </div>
          )}
        </Section>

        <Section
          title="Simulated Purchase"
          subtitle="In the real flow, this fires after Stripe processes payment."
        >
          <div class="mb-4 rounded-md bg-gray-50 p-4">
            <p class="font-medium text-gray-900">{DEMO_COURSE.title}</p>
            <p class="mt-1 text-lg font-bold text-gray-900">{DEMO_COURSE.price}</p>
          </div>
          {!purchaseResult ? (
            <button
              type="button"
              class="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              onClick={handlePurchase}
            >
              Complete Purchase (Simulated)
            </button>
          ) : (
            <div class="flex items-center gap-2 text-sm font-medium text-green-700">
              <span>&#10003;</span> Purchase simulated &mdash; see results below
            </div>
          )}
        </Section>

        <Section
          title="What Fires on Purchase"
          subtitle={
            purchaseResult
              ? 'These notifications tell Ontraport that a sale happened on Playground.'
              : undefined
          }
        >
          {!purchaseResult ? (
            <p class="text-sm text-gray-400">Click "Complete Purchase" above to see what fires.</p>
          ) : (
            <div class="flex flex-col gap-5">
              <div class="flex flex-col gap-1">
                <CheckItem
                  label="Partner tracking pixel sent to Ontraport"
                  detail="Tells Ontraport a purchase happened. Ontraport matches the buyer's email to their contact record (which has the affiliate from the original opt-in) and can calculate commissions."
                  done
                />
                <CheckItem
                  label="Ontraport API call: contact tagged as purchased"
                  detail="Server-side call to the Ontraport API looks up the contact by email and tags them. Works even if cookies are gone (different device, cleared cookies, etc.)."
                  done
                />
                <CheckItem label="Server log recorded" detail="For troubleshooting." done />
              </div>

              <div>
                <h3 class="mb-1 text-sm font-medium text-gray-700">
                  Partner Tracking Pixel (sent to Ontraport)
                </h3>
                <JsonBlock data={purchaseResult.pixelPayload} />
              </div>

              <div>
                <h3 class="mb-1 text-sm font-medium text-gray-700">
                  Ontraport API Call (server-side)
                </h3>
                <JsonBlock data={purchaseResult.apiPayload} />
              </div>

              <div>
                <h3 class="mb-1 text-sm font-medium text-gray-700">Server Log</h3>
                <pre class="overflow-x-auto rounded-md bg-gray-50 p-4 text-xs text-gray-700">
                  {purchaseResult.serverLog}
                </pre>
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
